/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDockerHost, getDockerContainerStats, DockerHost } from "@/api/docker";
import { useDockerHost } from "@/hooks/useDockerHost";
import { Badge } from "@/components/ui/badge";
import { MiniAreaChart } from "@/components/common/MiniAreaChart";
import { CHART_COLORS } from "@/constants/chartColors";
import { toResourcePoints } from "@/utils/dockerStats";
import { WidgetCard, WidgetAccent } from "./WidgetCard";

interface DockerHostWidgetProps {
  hostId: number;
}

export const DockerHostWidget: React.FC<DockerHostWidgetProps> = ({ hostId }) => {
  const { data: host, isLoading: isLoadingHost } = useQuery({
    queryKey: ["docker-host", hostId],
    queryFn: () => getDockerHost(hostId),
  });

  if (isLoadingHost || !host) {
    return (
      <WidgetCard title="Docker">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </WidgetCard>
    );
  }

  // Split into an inner component so useDockerHost (which reads host.id
  // unconditionally) is never called with a still-loading/undefined host.
  return <DockerHostWidgetInner host={host} />;
};

const DockerHostWidgetInner: React.FC<{ host: DockerHost }> = ({ host }) => {
  const { containers, isLoadingContainers } = useDockerHost({ host });

  const runningCount = containers.filter((c) => c.state === "running").length;
  const stoppedCount = containers.length - runningCount;

  // Chart the single busiest running container as a representative trend --
  // a true host-level aggregate across every container would need a new
  // backend aggregation endpoint, out of scope for this pass.
  const topContainer = containers
    .filter((c) => c.state === "running")
    .sort((a, b) => (b.latest?.cpuPercent ?? 0) - (a.latest?.cpuPercent ?? 0))[0];

  const { data: statsHistory } = useQuery({
    queryKey: ["docker-container-stats", host.id, topContainer?.containerId],
    queryFn: () => getDockerContainerStats(host.id, topContainer!.containerId, 1, 30),
    enabled: !!topContainer,
  });

  const cpuHistory = statsHistory
    ? toResourcePoints(statsHistory.data).map((p) => ({ time: p.time, value: p.cpuPercent }))
    : [];

  const accent: WidgetAccent = containers.length === 0
    ? "neutral"
    : runningCount === 0
      ? "error"
      : stoppedCount > 0
        ? "warn"
        : "ok";

  return (
    <WidgetCard title={host.name} accent={accent}>
      {isLoadingContainers ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading containers...</p>
      ) : (
        <div className="flex items-center gap-3">
          <Badge variant="success">{runningCount} running</Badge>
          {stoppedCount > 0 && <Badge variant="secondary">{stoppedCount} stopped</Badge>}
        </div>
      )}
      {topContainer && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
            {topContainer.name} (CPU)
          </p>
          <MiniAreaChart
            data={cpuHistory}
            color={CHART_COLORS.cpu}
            gradientId={`docker-cpu-${host.id}`}
            height={70}
            valueFormatter={(v) => `${v.toFixed(0)}%`}
            emptyLabel="No history yet"
          />
        </div>
      )}
    </WidgetCard>
  );
};
